/**
 * Workspace v3.x Brief 3 — checklist step deep-links.
 *
 * The two Assigned-stage steps point at the existing tools so the supervisor
 * reaches them at the right moment in the workflow (the actual gap — the tools
 * exist, just unlinked): "Send intro email…" → the Email Drafter, "Add the CM's
 * photo + bio…" → the People bio/photo editor.
 *
 * Keyed on `link_key` (migration 0154) — the tenant-neutral, rename-safe anchor
 * carried from the template item onto every instance. `link_key` is the slug of
 * the step's ORIGINAL label and is deliberately NEVER recomputed on a re-label
 * (see workspaceProcesses.updateTemplateItem), so renaming a step in the process
 * editor keeps its deep-link.
 *
 * v1 of this module keyed on the label STRING itself, which is exactly the break
 * 0154 shipped link_key to close: renaming "CM bio + photo" → "Staff bio & photo
 * added" (to cover SPICE staff too) silently dropped the link, because the map
 * lookup missed and the step fell back to plain text. Step labels are
 * operator-editable workflow text; link_key is not.
 *
 * The step keys below are PRODUCT-defined workflow anchors (not tenant business
 * data), so this map stays §3-clean.
 */

/** The identity fields a checklist step exposes to the deep-link resolver. */
export interface ChecklistStepIdentity {
  /** 0154 rename-safe anchor. Null only on rows predating the backfill. */
  link_key: string | null;
  label: string;
}

// link_key → route builder. Keys are the 0154 slug of the step's ORIGINAL label
// (slug rule: lower-cased, every non-alphanumeric run collapsed to '_', trimmed).
const STEP_ROUTES_BY_KEY: Record<string, (slug: string) => string> = {
  // v2 (0124) — stable across any later rename of the step's label.
  introduction_email_sent: (slug) => `/t/${slug}/ai-tools/email`,
  cm_bio_photo: (slug) => `/t/${slug}/admin-review?tab=people`,
  // v1 — CLOSED/archived cases keep their historical (pre-resync) rows; 0154
  // backfilled their link_key from the v1 labels. The Completed-view drawer
  // still renders them.
  send_intro_email_to_the_family: (slug) => `/t/${slug}/ai-tools/email`,
  add_the_cm_s_photo_bio_to_their_profile: (slug) => `/t/${slug}/admin-review?tab=people`,
};

// Label fallback — ONLY for rows whose link_key is null (pre-0154 leftovers).
// Deliberately the SEEDED labels, never renamed ones: a step that has a
// link_key resolves through the map above or not at all.
const STEP_ROUTES_BY_LABEL: Record<string, (slug: string) => string> = {
  'Introduction email sent': (slug) => `/t/${slug}/ai-tools/email`,
  'CM bio + photo': (slug) => `/t/${slug}/admin-review?tab=people`,
  'Send intro email to the family': (slug) => `/t/${slug}/ai-tools/email`,
  "Add the CM's photo + bio to their profile": (slug) => `/t/${slug}/admin-review?tab=people`,
};

/**
 * The in-app route a checklist step deep-links to, or null when the step carries
 * no link. Resolves on the step's rename-safe link_key, falling back to the
 * seeded label only when link_key is absent. Pure — drives a conditional <Link>
 * in StageChecklist.
 */
export function checklistItemRoute(step: ChecklistStepIdentity, tenantSlug: string): string | null {
  if (step.link_key !== null) {
    const byKey = STEP_ROUTES_BY_KEY[step.link_key];
    return byKey ? byKey(tenantSlug) : null;
  }
  const byLabel = STEP_ROUTES_BY_LABEL[step.label];
  return byLabel ? byLabel(tenantSlug) : null;
}
